import { localDate, startMinute, type Opportunity } from '../opportunity';

/** Day, then start time — the order events are stored and read in. */
/** How long a hidden card takes to fade and close its gap. Matches `Leave` in EventList. */
export const HIDE_MS = 440;

/**
 * A phone swipe's confirmation ("Added to Schedule"): how long it shows once the
 * card is off the screen, then how long it takes to fade. Only after both does
 * the card's row close, or, when the card is still listed, the card come back.
 */
export const SWIPE_CONFIRM_MS = 2000;
export const SWIPE_FADE_MS = 400;

export const byWhen = (a: Opportunity, b: Opportunity) =>
  localDate(a).localeCompare(localDate(b)) || (startMinute(a) ?? -1) - (startMinute(b) ?? -1);

export const FEEDBACK_URL =
  (import.meta.env.VITE_FEEDBACK_FORM_URL as string | undefined) ??
  'https://forms.gle/EMdaVKtjdm8jxN4W9';

/**
 * The lockup: the CommonGround logo, tree and wordmark in one line
 * (`public/commonground-logo.svg`, 422 x 68), or the tree alone on a phone
 * (`commonground-mark.svg`). Widths in pixels; the height follows.
 * `min` is where it stops shrinking in the top strip: below it the wordmark
 * stops reading.
 */
export const LOGO_W = { wide: 236, narrow: 184, min: 150, phone: 30 };
export const LOGO_ASPECT = 422.05 / 68.07;

/** Remembers that the swipe has been demonstrated once on this browser. */
export const SWIPE_HINT_KEY = 'cg.swipeHintSeen';

/**
 * The top strip's measurements, from 900px up. They live here rather than in
 * `Frame` because the lockup's own clamp is written here and `Frame` reads this
 * file for the lockup's size: one of the two has to hold them.
 */
/** The strip's side padding. */
export const STRIP_X = 26;
/** What the view toggle keeps clear of on either side: the filters, the lockup, the links, the callout. */
export const TOGGLE_GAP = 16;

/** In the left sidebar: full size until the sidebar is narrower than that, then exactly its width. */
export const LOCKUP_IN_SIDEBAR = `min(${LOGO_W.wide}px, 100cqw)`;

/**
 * In the top strip, which starts at the page's left edge whenever the lockup
 * is in it: the room left of the view toggles at the screen's centre (the
 * strip's 26px padding, the pair's reach left of centre and the 16px gap), down to a
 * floor below which the wordmark stops reading. Below 900px the floor binds,
 * and `stripLockupRight` is where it lands; the toggle is then pushed clear of
 * it rather than the lockup giving up more. A toggle pushed right by the
 * filters only leaves more room, and is deliberately not fed back in here.
 */
export const LOCKUP_IN_STRIP = `clamp(${LOGO_W.min}px, calc(50vw - ${STRIP_X + TOGGLE_GAP}px - var(--toggle-l)), ${LOGO_W.wide}px)`;
